import DangerIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import React from 'react';
import { IconMap } from '../Sidebar/IconHelper';
import { Palette } from 'Palette';
import { MODAL_CLOSE_BUTTON, RADIUS } from 'DesignTokens';
import StyledSection from './StyledSection';
import { StyledHeading, StyledTypography } from './styles';

type StyledModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  tone?: keyof typeof Palette;
  className?: string;
  /** Show an alert-style top bar when provided */
  type?: 'warning' | 'danger';
  noTopBar?: boolean;
  noPadding?: boolean;
  hideCloseButton?: boolean;
  disableClickAwayClose?: boolean;
  /**
   * 'center' (default) suits a short confirmation-style modal. A wide, busy modal (a working
   * tool rather than a yes/no prompt) reads better with its title and actions aligned like an
   * ordinary page instead of centered.
   */
  align?: 'center' | 'left';
} & React.ComponentProps<typeof Dialog>;

const SIDE_PAD = '1.5rem';

const StyledModal: React.FC<StyledModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  tone = 'default',
  className,
  type,
  noTopBar = false,
  noPadding = false,
  hideCloseButton = false,
  disableClickAwayClose = false,
  align = 'center',
  ...rest
}) => {
  const CloseIcon = IconMap.Close.iconRounded;
  const isAlert = !!type;
  const barColor = type === 'warning' ? Palette.warning.buttonBg : Palette.danger.buttonBg;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (disableClickAwayClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown={disableClickAwayClose}
      className={`react-StyledModal ${className || ''}`.trim()}
      PaperProps={{
        sx: {
          borderRadius: RADIUS.modal,
          position: 'relative',
          // IMPORTANT: clip contents so alert bar respects rounded corners
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
      {...rest}
    >
      {/* Close button */}
      {!hideCloseButton && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            backgroundColor: MODAL_CLOSE_BUTTON.background,
            border: MODAL_CLOSE_BUTTON.border,
            borderRadius: MODAL_CLOSE_BUTTON.borderRadius,
            width: 32,
            height: 32,
            p: 0,
            zIndex: 2,
            boxShadow: MODAL_CLOSE_BUTTON.boxShadow,
            color: MODAL_CLOSE_BUTTON.color,
            '&:hover': {
              backgroundColor: MODAL_CLOSE_BUTTON.hoverBackground,
              borderColor: MODAL_CLOSE_BUTTON.hoverBorder,
            },
            '&:focus': { outline: 'none' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      {/* Optional alert top bar (kept INSIDE the paper) */}
      {!noTopBar && isAlert && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: barColor,
            px: SIDE_PAD,
            py: '0.75rem',
          }}
        >
          {type === 'warning' ? <WarningIcon sx={{ mr: 1 }} /> : <DangerIcon sx={{ mr: 1 }} />}
          <StyledTypography variant="subtitle1">
            {type!.charAt(0).toUpperCase() + type!.slice(1)}
          </StyledTypography>
        </Box>
      )}

      {/* Body */}
      <StyledSection
        tone={tone}
        noTopPadding
        noSidePaddingFrom={noPadding ? 'xs' : undefined}
        bottomSpacing={noPadding ? 0 : undefined}
        // When alert bar is shown above, only round the bottom so seams are clean
        sx={{ borderRadius: isAlert ? `0 0 ${RADIUS.modal} ${RADIUS.modal}` : RADIUS.modal }}
      >
        {(title || subtitle) && (
          // left align: no extra px here beyond StyledSection's own side padding, the same
          // inset `children` gets from DialogContent's p:0 -- so the title's left edge lines
          // up with the body's content instead of sitting further in than it.
          <Box
            sx={{ px: align === 'left' ? 0 : SIDE_PAD, pt: '1.5rem', pb: '1rem', textAlign: align }}
          >
            {title && (
              <StyledHeading
                headerVariant="heading2"
                tone={tone}
                sx={{ fontWeight: 700, fontSize: '1.25rem' }}
              >
                {title}
              </StyledHeading>
            )}
            {subtitle && (
              <StyledHeading
                headerVariant="subheading"
                tone={tone}
                sx={{ fontSize: '1rem', mt: 0.5 }}
              >
                {subtitle}
              </StyledHeading>
            )}
          </Box>
        )}

        {!title && !subtitle && <Box sx={{ pt: '1.5rem' }} />}

        <DialogContent sx={{ p: 0 }}>{children}</DialogContent>

        {actions && (
          <DialogActions
            sx={{
              mt: 3,
              px: align === 'left' ? 0 : SIDE_PAD,
              pb: '1.5rem',
              justifyContent: align === 'left' ? 'flex-start' : 'center',
            }}
          >
            {actions}
          </DialogActions>
        )}
      </StyledSection>
    </Dialog>
  );
};

export default StyledModal;
